import _styled from "styled-components";
import type { SanityArticleCard } from "@ouster/shared";
import { nonNullable, stripHtmlTags, takeFirst } from "~/features/app/utils";
import { up } from "~/features/app/screens";
import { EditorialCard } from "~/features/card/editorial-card";
import { EditorialFeaturedCard } from "~/features/card/editorial-featured-card";
import { type Ratio } from "~/features/ui/aspect-ratio";
import { Tag } from "~/features/ui/tag";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { hasRenderableSanityMedia, SanityMediaFigure } from "~/features/sanity/page-builder/sections/media-figure";
import { toHtml } from "~/features/sanity/rich-text/to-html";
export type BlogArticleCardProps = {
  article: SanityArticleCard;
  featured?: boolean;
  size?: "regular" | "large";
  ratio?: "3/2" | "16/9" | "fill";
};
function cardText(article: SanityArticleCard) {
  const hero = takeFirst(article.hero);
  const html = hero?.text?.length ? toHtml(hero.text) : undefined;
  return html ? stripHtmlTags(html) : undefined;
}

// Uses the `ArticleTags` markup (`<ul>` of blurred `Tag`s) with the same
// way the editorial-cards adapter does: `[category, industry]`.
function cardTags(article: SanityArticleCard) {
  const items = [article.category, article.industry].filter(nonNullable);
  if (!items.length) {
    return undefined;
  }
  return <_StyledUl>
      {items.map((item, index) => <Tag as="li" key={item._id ?? index} variant="blur">
          {item.title}
        </Tag>)}
    </_StyledUl>;
}
function cardMedia(article: SanityArticleCard, ratio: Ratio | undefined, fit: "cover" | "fill") {
  const heroMedia = takeFirst(article.hero)?.media;
  const media = hasRenderableSanityMedia(heroMedia) ? heroMedia : article.articleLegacyImage;
  if (!hasRenderableSanityMedia(media)) {
    return undefined;
  }
  return <SanityMediaFigure media={media} ratio={ratio} fit={fit} sizes="lg:33vw" />;
}

/**
 * Builds
 * `EditorialCard`/`EditorialFeaturedCard` props from a `SanityArticleCard`.
 */
export function BlogArticleCard(props: BlogArticleCardProps) {
  const {
    article,
    featured,
    size = "regular",
    ratio: _ratio = "3/2"
  } = props;
  const isLg = useMediaQuery(up("lg"));
  const ratio = isLg ? _ratio === "fill" ? undefined : _ratio : "3/2";
  const fit = isLg ? ratio ? "fill" : "cover" : "fill";
  const headline = article.headline || article.title || undefined;
  const url = article.href || undefined;
  const text = cardText(article);
  const media = cardMedia(article, ratio, fit);
  const tags = cardTags(article);
  if (featured) {
    return <EditorialFeaturedCard headline={headline} text={text} url={url} linkText="Read More" size={size} media={media} tags={tags} />;
  }
  return <EditorialCard headline={headline} text={text} url={url} linkText="Read More" media={media} tags={tags} />;
}
var _StyledUl = /*#__PURE__*/_styled("ul").withConfig({
  displayName: "article-card___StyledUl",
  componentId: "sc-114paqo-0"
})({
  "position": "relative",
  "zIndex": "10",
  "display": "flex",
  "gap": "0.4rem"
});